import { extends as _extends, objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, typeof as _typeof, objectSpread2 as _objectSpread2 } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import React, { useRef, useState, useMemo, useCallback } from 'react';
import createComponent from '../../../styles-system/create-component.esm.js';
import { ellipsis } from '../../../themes/const.esm.js';
import { positions } from '../../../utils/props-utils.esm.js';
import { reactNodeToString, mergeRefs } from '../../../utils/react-helpers.esm.js';

var _excluded = ["__themeKey", "variant", "as", "__css"],
  _excluded2 = ["className", "lhCrop", "title", "children", "__css", "postContent", "ellipsis"];
function shouldApplyEllipsisCss(props) {
  return typeof props.ellipsis === 'boolean' && props.ellipsis;
}
function isEllipsisConfig(subject) {
  return !!subject && _typeof(subject) === 'object' &&
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (subject === null || subject === void 0 ? void 0 : subject.rows) > 1;
}
var TextElements = ['p', 'a', 'pre', 'code', 'kbd', 'samp', 'abbr', 'i', 'em', 'var', 'cite', 'strong', 'sub', 'sup', 'ins', 'u', 'mark', 'b'];
var StyledText = /*#__PURE__*/React.forwardRef(function StyledText(props, ref) {
  var _props$__themeKey = props.__themeKey,
    __themeKey = _props$__themeKey === void 0 ? 'text' : _props$__themeKey,
    variant = props.variant,
    _props$as = props.as,
    component = _props$as === void 0 ? 'span' : _props$as,
    __css = props.__css,
    rest = _objectWithoutProperties(props, _excluded);
  var Component = useMemo(function () {
    return createComponent(component, 'csg-text');
  }, [component]);
  var _variant = variant ? variant : TextElements.includes(component) ? "styles.".concat(component) : undefined;
  var styles = useMemo(function () {
    return _objectSpread2(_objectSpread2({}, positions(props)), __css);
  }, [__css, props]);
  return /*#__PURE__*/React.createElement(Component, _extends({
    ref: ref,
    __css: styles
  }, rest, {
    variant: _variant,
    __themeKey: __themeKey
  }));
});
var defaultTextStyle = {
  wordBreak: 'normal'
};
var lineClampStyle = {
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  lineHeight: '1.25'
};
var TextPrivate = /*#__PURE__*/React.forwardRef(function _TextPrivate(props, ref) {
  var className = props.className,
    lhCrop = props.lhCrop,
    _title = props.title,
    children = props.children,
    _props$__css = props.__css,
    __css = _props$__css === void 0 ? {} : _props$__css,
    postContent = props.postContent,
    ellipsisProps = props.ellipsis,
    rest = _objectWithoutProperties(props, _excluded2);
  var applyTextEllipsisCss = shouldApplyEllipsisCss(props);
  var localRef = useRef(null);

  // Define state and function to update the value
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showTitle = _useState2[0],
    setShowTitle = _useState2[1];
  var ellipsisConfig = isEllipsisConfig(ellipsisProps) ? ellipsisProps : undefined;
  var title = useMemo(function () {
    return (_title !== null && _title !== void 0 ? _title : applyTextEllipsisCss || ellipsisConfig) ? "".concat(reactNodeToString(children)).concat(postContent ? ' ' + postContent : '') : '';
  }, [_title, applyTextEllipsisCss, children, postContent, ellipsisConfig]);
  var handleOnMouseOver = useCallback(function () {
    var compare = !!localRef.current && (localRef.current.scrollWidth > localRef.current.clientWidth || !!(ellipsisConfig !== null && ellipsisConfig !== void 0 && ellipsisConfig.rows) && localRef.current.scrollHeight > localRef.current.clientHeight);
    setShowTitle(compare);
  }, [ellipsisConfig === null || ellipsisConfig === void 0 ? void 0 : ellipsisConfig.rows]);
  var wrap = lhCrop && applyTextEllipsisCss;

  // eslint-disable-next-line no-console
  // console.log(`----- render text [${children}]-----`)
  var styles = useMemo(function () {
    var _ellipsisConfig$rows;
    return _objectSpread2(_objectSpread2(_objectSpread2(_objectSpread2({}, defaultTextStyle), applyTextEllipsisCss ? ellipsis : {}), ellipsisConfig ? _objectSpread2(_objectSpread2({}, lineClampStyle), {}, {
      WebkitLineClamp: (_ellipsisConfig$rows = ellipsisConfig.rows) !== null && _ellipsisConfig$rows !== void 0 ? _ellipsisConfig$rows : 2
    }) : {}), __css);
  }, [__css, applyTextEllipsisCss, ellipsisConfig]);
  var content = /*#__PURE__*/React.createElement(StyledText, _extends({
    ref: wrap ? localRef : mergeRefs(ref, localRef),
    className: wrap ? 'csg-tui-text-lhCrop' : classNames('csg-tui-text', className),
    __css: styles,
    title: showTitle ? title !== null && title !== void 0 ? title : '' : '',
    onMouseOver: handleOnMouseOver
  }, wrap ? {} : _objectSpread2({
    lhCrop: lhCrop
  }, rest)), children);
  return wrap ? /*#__PURE__*/React.createElement(StyledText, _extends({
    className: classNames('csg-tui-text', className),
    ref: ref,
    lhCrop: lhCrop
  }, rest), content) : content;
});
var MemoText = /*#__PURE__*/React.memo(TextPrivate);
var Text = /*#__PURE__*/React.forwardRef(function Text(props, ref) {
  return /*#__PURE__*/React.createElement(MemoText, _extends({
    ref: ref
  }, props));
});

export default Text;
export { TextPrivate, shouldApplyEllipsisCss };
//# sourceMappingURL=text.esm.js.map
